import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { RootLayout } from "./routes/root-layout";
import { MailShell } from "./routes/mail/mail-shell";
import { MailIndexPane } from "./routes/mail/mail-index-pane";
import { ThreadDetailRoute } from "./routes/mail/thread-detail-route";
import { AdminPage } from "./routes/admin/admin-page";
import { LoginPage } from "./routes/auth/login-page";
import { ForgotPasswordPage } from "./routes/auth/forgot-password-page";
import { ResetPasswordPage } from "./routes/auth/reset-password-page";

const rootRoute = createRootRoute({ component: RootLayout });

export interface MailSearchParams {
  folder?: string;
  q?: string;
  /**
   * Which mailbox is "active" (Phase 5 mailbox switcher) — a mailbox id, or
   * the sentinel "all" for the cross-mailbox aggregate view. Lives as a URL
   * search param (not path segment, like `folder`/`q`) so it persists
   * across navigating into and back out of a thread, and so a link straight
   * into a specific thread from the aggregate view can carry it. See
   * hooks/use-mailbox.ts.
   */
  mailbox?: string;
}

function validateMailSearch(search: Record<string, unknown>): MailSearchParams {
  return {
    folder: typeof search.folder === "string" ? search.folder : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    mailbox: typeof search.mailbox === "string" ? search.mailbox : undefined,
  };
}

// Pathless layout route: contributes the two-pane shell (list + Outlet) but
// no URL segment of its own — its children own the actual paths, so the
// app's URLs are "/" and "/$threadId", not "/mail" and "/mail/$threadId".
// `folder`/`q` search params (see mail-shell.tsx) live on these URLs and
// persist across navigating into and back out of a thread.
const mailLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "mail-layout",
  component: MailShell,
  validateSearch: validateMailSearch,
});

const mailIndexRoute = createRoute({
  getParentRoute: () => mailLayoutRoute,
  path: "/",
  component: MailIndexPane,
});

const threadDetailRoute = createRoute({
  getParentRoute: () => mailLayoutRoute,
  path: "/$threadId",
  component: ThreadDetailRoute,
});

// Owner-only admin surface (provision mailboxes, manage ACLs) — a real
// top-level route (not nested under mailLayoutRoute) since it doesn't need
// the two-pane mail shell's folder rail/search bar/compose FAB chrome.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});

// Phase 8 (password auth): custom-branded login/reset pages, replacing
// Cloudflare Access's hosted login entirely — real top-level routes (not
// nested under mailLayoutRoute, same reasoning as /admin above: no folder
// rail/search/compose chrome makes sense before a user is even signed in).
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

interface ResetPasswordSearchParams {
  token?: string;
}

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearchParams => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
});

const routeTree = rootRoute.addChildren([
  mailLayoutRoute.addChildren([mailIndexRoute, threadDetailRoute]),
  adminRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
